import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import type { DashboardRouteModel } from 'tio-employee/routes/authenticated/dashboard';
import type FeaturesService from 'tio-common/services/features';
import DashboardGridLayout from 'tio-employee/components/dashboard/grid-layout';
import DashboardPhoneCaptureAlert from 'tio-employee/components/dashboard/phone-capture-alert';
import DashboardAddLoan from 'tio-employee/components/dashboard/widgets/add-loan';
import DashboardCommonQuestions from 'tio-employee/components/dashboard/widgets/common-questions';
import DashboardConsolidationRefinance from 'tio-employee/components/dashboard/widgets/consolidation-refinance';
import DashboardEmployeeAgent from 'tio-employee/components/dashboard/widgets/employee-agent';
import DashboardFiveTwoNineTools from 'tio-employee/components/dashboard/widgets/five-two-nine-tools';
import DashboardGlossary from 'tio-employee/components/dashboard/widgets/glossary';
import DashboardIncomeDrivenRepayment from 'tio-employee/components/dashboard/widgets/income-driven-repayment';
import DashboardLoanForgiveness from 'tio-employee/components/dashboard/widgets/loan-forgiveness';
import DashboardLoanSummary from 'tio-employee/components/dashboard/widgets/loan-summary';
import DashboardMentoring from 'tio-employee/components/dashboard/widgets/mentoring';
import DashboardPayingForCollege from 'tio-employee/components/dashboard/widgets/paying-for-college';
import DashboardPublicServiceLoanForgiveness from 'tio-employee/components/dashboard/widgets/public-service-loan-forgiveness';
import DashboardRepaymentPlans from 'tio-employee/components/dashboard/widgets/repayment-plans';
import DashboardScholarship from 'tio-employee/components/dashboard/widgets/scholarship';
import DashboardSecureYourFuture from 'tio-employee/components/dashboard/widgets/secure-your-future';
import DashboardStarbucksScap from 'tio-employee/components/dashboard/widgets/starbucks-scap';
import DashboardStrategyFinder from 'tio-employee/components/dashboard/widgets/strategy-finder';
import DashboardStudentLoanCoaches from 'tio-employee/components/dashboard/widgets/student-loan-coaches';
import DashboardStudentLoanRepayment from 'tio-employee/components/dashboard/widgets/student-loan-repayment';
import DashboardTuitionAssistance from 'tio-employee/components/dashboard/widgets/tuition-assistance';
import DashboardPublicServiceLoanForgivenessAdmin from 'tio-employee/components/dashboard/widgets/public-service-loan-forgiveness-admin';
import DashboardTuitionAssistanceAdmin from 'tio-employee/components/dashboard/widgets/tuition-assistance-admin';
import DashboardLearningPartnerNetwork from 'tio-employee/components/dashboard/widgets/learning-partner-network';
import DashboardCollegePlanningMentors from 'tio-employee/components/dashboard/widgets/college-planning-mentors';
import DashboardWellnessInsights from 'tio-employee/components/dashboard/widgets/wellness-insights';
import DashboardCareerPathways from 'tio-employee/components/dashboard/widgets/reimagined';
import BetaHome from 'tio-employee/components/beta/home';
import TioLoadingSpinner from 'tio-common/components/tio/loading-spinner';
import { getPromiseState } from '@warp-drive/core/reactive';
import type { BetaHomeRouteModel } from 'tio-employee/routes/authenticated/dashboard';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import { modifier } from 'ember-modifier';
import {
  LOGIN_TO_DASHBOARD_TIMING,
  markDashboardRendered,
} from 'tio-employee/utils/perf/login-to-dashboard';
import type TelemetryService from 'tio-common/services/telemetry';

// Calls back on the first frame after the element is inserted — the dashboard is on screen.
const onFirstFrame = modifier((_element: Element, [callback]: [() => void]) => {
  const frame = requestAnimationFrame(callback);
  return () => cancelAnimationFrame(frame);
});

export interface S {
  Args: {
    model: DashboardRouteModel;
  };
}
export default class DashboardRoute extends Component<S> {
  @service declare partner: PartnerService;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare features: FeaturesService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;
  @service declare telemetry: TelemetryService;

  model!: unknown;

  reportRendered = () => {
    const duration = markDashboardRendered();
    if (duration !== null) {
      this.telemetry.trackTiming(LOGIN_TO_DASHBOARD_TIMING, duration);
    }
  };

  /**
   * Reactive state of the beta home load, present only when the beta opt-in is
   * on — the route adds an unresolved `beta` promise to its model in that case
   * and omits it otherwise.
   */
  get betaState() {
    if (this.betaExperience.isEnabled && 'beta' in this.args.model) {
      return getPromiseState((this.args.model as { beta: Promise<BetaHomeRouteModel> }).beta);
    }
    return undefined;
  }

  get betaModel(): BetaHomeRouteModel | undefined {
    return this.betaState?.result ?? undefined;
  }

  get isBetaPending() {
    return this.betaState?.isPending ?? false;
  }

  get isReimaginedEnabled() {
    return this.features.isEnabled('showReimaginedOptIn');
  }

  get hasAccounts() {
    return !!this.sessionContext.user?.person?.accounts?.length;
  }

  get needsToLinkLoans() {
    return !!this.sessionContext.user?.person && !this.hasAccounts;
  }

  get isFamilyMemberView() {
    return this.sessionContext.isFamilyMember;
  }

  get isPslfEnabled() {
    return this.partner.isPslfEnabled && !this.employee.hasRogueFein;
  }

  get isFaqEnabled() {
    return this.partner.isFaqEnabled;
  }

  get hasContributions() {
    return this.employee.hasContributions;
  }

  get isFamilyPlanEnabled() {
    return this.partner.doesAllowFamilyPlan;
  }

  get isTaMentoringEnabled() {
    return this.partner.companySettings?.tuitionAssistance?.isTaMentoringEnabled;
  }

  get shouldShowTuitionAssistanceTile() {
    if (
      this.features.isEnabled('tuitionAssistance') &&
      this.partner.hasNewTuitionAssistance &&
      this.partner.company?.taEnabled &&
      this.employee.employee.tasParticipant?.isActive
    ) {
      return true;
    }

    return !!this.sessionContext.currentEmployee?.tasEligibilities?.length;
  }

  get isIdrEnabled() {
    return this.partner.isIdrEnabled;
  }

  get isSyfEnabled() {
    return this.partner.companySettings.modules?.syf?.isEnabled;
  }

  get isPslfEligible() {
    return this.partner.isPslfEligible;
  }

  get isPrioritizedStrategyFinder() {
    // Prioritize SF widget for partners who are PSLF_ELIGIBLE but have not purchased PSLF product
    return this.isPslfEligible && !this.isPslfEnabled;
  }

  get isDeprioritizedStrategyFinder() {
    return !this.isPrioritizedStrategyFinder;
  }

  get isContributionPlan() {
    return this.employee.current.plan && this.employee.current.plan.cadence !== 'NEVER';
  }

  get isSlrEnabled() {
    return this.isContributionPlan;
  }

  get isDashboardAddLoanEnabled() {
    return this.isContributionPlan ? false : this.needsToLinkLoans;
  }

  get isPslfAdmin() {
    return !!this.sessionContext.rolesForCurrentCompany.find(
      (e) => e.relationshipType === 'PSLF_ADMIN'
    );
  }

  get isTAAdmin() {
    return this.sessionContext.rolesForCurrentCompany.some(
      (role) =>
        role.relationshipType.startsWith('TAS.Approver') ||
        role.relationshipType === 'TAS.Admin.SUPERUSER'
    );
  }

  get isLearningPartnerNetworkEnabled() {
    const companySettings = this.partner.companySettings;
    return companySettings.lpn?.isEnabled ?? false;
  }

  get familyMemberWidgets() {
    return (
      [
        this.isDashboardAddLoanEnabled ? DashboardAddLoan : undefined,
        this.isPrioritizedStrategyFinder ? DashboardStrategyFinder : undefined,
        this.hasAccounts ? DashboardLoanSummary : undefined,
        this.isLearningPartnerNetworkEnabled ? DashboardLearningPartnerNetwork : undefined,
        this.partner.isStarbucks ? DashboardStarbucksScap : undefined,
        DashboardStudentLoanCoaches,
        this.isDeprioritizedStrategyFinder ? DashboardStrategyFinder : undefined,
      ].filter((val) => val !== undefined && val !== null) || []
    );
  }

  get sortedWidgets() {
    if (this.partner.isHoraceMann) {
      return this.horaceMannWidgets;
    }
    if (this.args.model.isTaOnly) {
      return [
        this.isTAAdmin ? DashboardTuitionAssistanceAdmin : undefined,
        this.shouldShowTuitionAssistanceTile ? DashboardTuitionAssistance : undefined,
        this.isTaMentoringEnabled ? DashboardMentoring : undefined,
        this.isLearningPartnerNetworkEnabled ? DashboardLearningPartnerNetwork : undefined,
      ];
    }

    if (this.isFamilyMemberView) {
      return this.familyMemberWidgets;
    }
    return (
      [
        this.isTAAdmin ? DashboardTuitionAssistanceAdmin : undefined,
        this.isPslfAdmin ? DashboardPublicServiceLoanForgivenessAdmin : undefined,
        this.isDashboardAddLoanEnabled ? DashboardAddLoan : undefined,
        this.shouldShowTuitionAssistanceTile ? DashboardTuitionAssistance : undefined,
        this.isSyfEnabled ? DashboardSecureYourFuture : undefined,
        this.isTaMentoringEnabled ? DashboardMentoring : undefined,
        this.isPslfEnabled ? DashboardPublicServiceLoanForgiveness : undefined,
        this.isSlrEnabled ? DashboardStudentLoanRepayment : undefined,
        this.isIdrEnabled ? DashboardIncomeDrivenRepayment : undefined,
        this.isPrioritizedStrategyFinder ? DashboardStrategyFinder : undefined,
        this.hasAccounts ? DashboardLoanSummary : undefined,
        this.isLearningPartnerNetworkEnabled ? DashboardLearningPartnerNetwork : undefined,
        this.partner.isStarbucks ? DashboardStarbucksScap : undefined,
        //DashboardLoanProjections,
        DashboardStudentLoanCoaches,
        DashboardLoanForgiveness,
        !this.partner.company?.taEnabled ? DashboardCollegePlanningMentors : undefined,
        DashboardWellnessInsights,
        DashboardPayingForCollege,
        DashboardRepaymentPlans,
        DashboardConsolidationRefinance,
        DashboardScholarship,
        DashboardFiveTwoNineTools,
        DashboardGlossary,

        this.isDeprioritizedStrategyFinder ? DashboardStrategyFinder : undefined,
        this.isFaqEnabled ? DashboardCommonQuestions : undefined,
        this.isReimaginedEnabled ? DashboardCareerPathways : undefined, // Tio Reimagined (non-production only)
      ].filter((val) => val !== undefined && val !== null) || []
    );
  }

  get horaceMannWidgets() {
    return (
      [
        this.isPslfEnabled ? DashboardPublicServiceLoanForgiveness : undefined,
        this.isDashboardAddLoanEnabled ? DashboardAddLoan : undefined,
        this.hasAccounts ? DashboardLoanSummary : undefined,
        this.isLearningPartnerNetworkEnabled ? DashboardLearningPartnerNetwork : undefined,
        DashboardStrategyFinder,
        DashboardEmployeeAgent,
        DashboardStudentLoanCoaches,
        DashboardLoanForgiveness,
      ].filter((val) => val !== undefined && val !== null) || []
    );
  }

  get taOnly() {
    return this.partner.isTaOnly;
  }

  <template>
    {{! No wrapper: a stack gap around a single child does nothing, and the
        `overflow-auto` it carried nested a scroll container inside the page. }}
    {{#if this.betaModel}}
      <BetaHome
        @model={{this.betaModel}}
        @wellnessData={{@model.wellnessData}}
        {{onFirstFrame this.reportRendered}}
      />
    {{else if this.isBetaPending}}
      <div class="tio-row-center py-20" aria-busy="true" data-test-beta-home-loading>
        <TioLoadingSpinner />
      </div>
    {{else}}
      <h1 class="sr-only">{{t "sidebar.home"}}</h1>
      <DashboardPhoneCaptureAlert />
      <DashboardGridLayout {{onFirstFrame this.reportRendered}}>
        {{! @glint-expect-error: return}}
        {{#each this.sortedWidgets as |widget|}}
          {{! @glint-expect-error: return}}
          {{#let (component widget) as |Widget|}}
            <Widget @personRequest={{@model.personRequest}} @wellnessData={{@model.wellnessData}} />
          {{/let}}
        {{/each}}
      </DashboardGridLayout>
    {{/if}}
  </template>
}
